import { useMap } from '@vis.gl/react-google-maps';
import {
    useEffect, useState,
} from 'react';

interface MapViewport {
    bbox: [number, number, number, number]; // [west, south, east, north]
    zoom: number;
}

interface UseMapViewportOptions {
    padding?: number;
}

function useMapViewport({ padding = 100 }: UseMapViewportOptions = {}): MapViewport {
    const map = useMap();
    const [viewport, setViewport] = useState<MapViewport>({ bbox: [-180, -85, 180, 85], zoom: 1 });

    useEffect(() => {
        if (!map) return undefined;

        const updateViewport = () => {
            const bounds = map.getBounds();
            const zoom = map.getZoom();
            if (!bounds || zoom == null) return;

            const ne = bounds.getNorthEast();
            const sw = bounds.getSouthWest();

            // Apply pixel padding to expand the bbox
            const projection = map.getProjection();
            if (projection) {
                const scale = 2 ** zoom;
                const nePx = projection.fromLatLngToPoint(ne);
                const swPx = projection.fromLatLngToPoint(sw);
                if (nePx && swPx) {
                    const paddingDeg = padding / scale;
                    setViewport({
                        bbox: [
                            sw.lng() - paddingDeg,
                            sw.lat() - paddingDeg,
                            ne.lng() + paddingDeg,
                            ne.lat() + paddingDeg,
                        ],
                        zoom: Math.round(zoom),
                    });
                    return;
                }
            }

            setViewport({
                bbox: [sw.lng(), sw.lat(), ne.lng(), ne.lat()],
                zoom: Math.round(zoom),
            });
        };

        updateViewport();
        const listener = map.addListener('idle', updateViewport);
        return () => listener.remove();
    }, [map, padding]);

    return viewport;
}

export default useMapViewport;
